'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Star } from 'lucide-react';
import { useState } from 'react';

import type { Course } from '@eliadmin/core/src/api/courses/coursers-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CategoryBadge } from '../jobportal/card/category-badge';
import { AddToFavourite } from '../jobportal/card/add-to-favorites';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { formatContentDate } from '@eliadmin/core/src/utils/date-utils';

// Extend the Course type to include metadata
type CourseWithMetadata = Course & {
  metadata?: {
    agencyName?: string;
    courseLocation?: string;
  };
};

export interface CourseCardSmallProps {
  course: CourseWithMetadata;
}

export const CourseCardSmall = ({ course }: CourseCardSmallProps) => {
  const { t } = useTranslations();
  const me = useCustomerStore(s => s.me);
  const showFavoriteButton = me ? true : false;
  const [imageError, setImageError] = useState(false);

  const isTopped = course?.topped_up_to && new Date(course.topped_up_to).getTime() > Date.now();
  const productPrice = course.product ? getProductPriceDisplay(course.product) : null;
  // Reuse core's UTC parsing, keeping this project's existing dd.MM.yyyy presentation.
  const publicationDate =
    formatContentDate(course.created_at, 'en-GB').replace(/\//g, '.') || '---';

  return (
    <div key={course.slug} className="flex w-full items-start justify-start">
      <div className="relative w-full">
        {showFavoriteButton && (
          <AddToFavourite
            course={course as any}
            type="course"
            className={{
              container: cn('absolute right-6 top-6 z-[10] h-9 w-9', isTopped && 'top-10'),
            }}
          />
        )}
        <Link
          href={course.slug}
          prefetchOnHover
          className={cn(
            'group flex h-[390px] w-full flex-col items-start justify-between overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition hover:shadow-md',
            isTopped && 'border-[3px] border-primary hover:border-primary',
          )}
        >
          <div className="flex w-full flex-col gap-2">
            <div className="relative h-[160px] min-h-[160px] w-full">
              <Image
                src={
                  imageError
                    ? '/images/placeholder.png'
                    : course?.image?.url || '/images/placeholder.png'
                }
                alt={resolveImageAlt((imageError ? undefined : course?.image), course?.name || 'Course')}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 300px"
                onError={() => setImageError(true)}
              />
              {!!isTopped && (
                <div className="absolute right-0 top-0 z-[10] flex h-[30px] w-auto items-center justify-center rounded-bl-lg bg-primary px-6 text-sm font-bold text-white">
                  TOP
                </div>
              )}
            </div>
            <div className="relative flex flex-col gap-2 p-4 pt-2">
              <div className="line-clamp-2 text-sm font-bold text-foreground">{course.name}</div>

              {/* Agency Name from Metadata */}
              {course?.metadata?.agencyName && course.metadata.agencyName.trim() && (
                <div className="line-clamp-1 text-xs text-muted-foreground">
                  {course.metadata.agencyName.split(',')[0].trim()}
                </div>
              )}

              {/* Course Location from Metadata */}
              {course?.metadata?.courseLocation && course.metadata.courseLocation.trim() && (
                <div className="flex items-center gap-1 text-xs text-primary">
                  <span>📍</span>
                  <span className="truncate">
                    {course.metadata.courseLocation.split(',')[0].trim()}
                  </span>
                </div>
              )}

              <div className="flex flex-wrap gap-1">
                {course?.categories?.slice(0, 2).map(category => (
                  <CategoryBadge
                    key={category?.id}
                    category={{
                      name: category?.name,
                      id: category?.id?.toString(),
                      slug: category?.name?.toLowerCase().replace(/\s+/g, '-') || '',
                    }}
                    maxWidth="100%"
                    asLink={false}
                  />
                ))}
              </div>
              {isTopped && (
                <Star className="absolute bottom-[-100px] right-[-40px] h-40 w-40 text-yellow-400 opacity-20" />
              )}
            </div>
          </div>
          <div className={cn('flex flex-col gap-2 p-4 pt-0')}>
            {/* Price - only show if not 0 */}
            {productPrice?.priceOnRequest && (
              <div className="mt-1 text-sm font-medium text-foreground">{t('text2278')}</div>
            )}
            {!productPrice?.priceOnRequest && (productPrice?.current ?? 0) > 0 && (
              <div className="mt-1 text-sm font-medium text-foreground">
                {formatPrice(productPrice?.current, '€')}
              </div>
            )}
            <div className={cn('text-[10px] text-muted-foreground')}>
              {t('Pridané')}: {publicationDate}
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};
